import { useQueryClient } from '@tanstack/react-query';
import { DropdownItem, DropdownLabel, DropdownSeparator, useToast } from '@waymail/ui';
import type { ConversaDaApi } from '../lib/api';
import { OPCOES_DE_ADIAMENTO, type QuandoAdiar } from '../lib/adiar';
import { useAdiar, useContas, useMensagem, usePastas, usePreferencias } from '../lib/consultas';
import { textoDoLembrete, useMarcarLembrete } from '../lib/lembretes';
import { rotularPastas } from '../lib/pastas';
import { adiarPara as adiarMensagens, responder as responderMensagem } from '../lib/acoesDoMenu';
import type { TipoDeResposta } from '../lib/resposta';
import { useTriagem } from '../lib/triagem';
import { useUi } from '../store/ui';
import { MenuDeContexto, type PontoDoMenu } from './MenuDeContexto';

/*
 * O menu de botao direito de uma linha da lista. So monta depois do primeiro
 * clique direito; responder, adiar e a triagem carregam sob demanda para o
 * bundle inicial caber no orcamento da Secao 14.1.
 *
 * Se a linha clicada faz parte da selecao, o menu age sobre a selecao inteira;
 * senao, so sobre ela. E o que o Outlook e o Gmail fazem, e evita arquivar
 * tres mensagens marcadas quando a pessoa clicou com o direito numa quarta.
 */

export function MenuDaMensagem({
  conversa,
  ponto,
  onFechar,
}: {
  conversa: ConversaDaApi;
  ponto: PontoDoMenu;
  onFechar: () => void;
}) {
  const cliente = useQueryClient();
  const toast = useToast();
  const { executar } = useTriagem();
  const adiar = useAdiar();
  const marcarLembrete = useMarcarLembrete();
  const contas = useContas();
  const preferencias = usePreferencias();
  const pastas = usePastas(conversa.accountId);
  // O endereco do remetente nao vem na lista; sai do cache se a mensagem ja foi aberta.
  const remetente = useMensagem(conversa.messageId).data?.from?.address ?? null;

  const marcadas = useUi((s) => s.checkedIds);
  const pastaAtual = useUi((s) => s.folderId);
  const limpar = useUi((s) => s.clearChecked);
  const selectMensagem = useUi((s) => s.selectMensagem);
  const mensagemAberta = useUi((s) => s.mensagemId);
  const abrirComposerCom = useUi((s) => s.abrirComposerCom);

  const emGrupo = marcadas.includes(conversa.messageId) && marcadas.length > 1;
  const ids = emGrupo ? marcadas : [conversa.messageId];
  const id = conversa.messageId;

  const destinos = rotularPastas(
    (pastas.data ?? []).filter(
      (p) => (p.role === null || p.role === 'inbox') && p.id !== pastaAtual,
    ),
  );

  /** Tira da tela o que saiu da lista: leitura aberta e selecao. */
  function aoSair() {
    if (mensagemAberta !== null && ids.includes(mensagemAberta)) selectMensagem(null);
    limpar();
  }

  function responder(tipo: TipoDeResposta) {
    void responderMensagem(
      {
        cliente,
        id,
        tipo,
        contas: contas.data ?? [],
        responderAcima: preferencias.data?.redacao.responderAcima ?? true,
        abrir: abrirComposerCom,
      },
      toast,
    );
  }

  function acompanharPara(quando: QuandoAdiar) {
    marcarLembrete.mutate(
      { messageId: id, quando },
      {
        onSuccess: () => toast({ title: textoDoLembrete(quando) }),
        onError: (e) => toast({ title: e.message, tone: 'error' }),
      },
    );
  }

  function adiarPara(quando: QuandoAdiar) {
    void adiarMensagens({ ids, quando, adiar: adiar.mutateAsync, aoConcluir: aoSair }, toast);
  }

  const sozinha = ids.length === 1;
  const sufixo = sozinha ? '' : ` (${ids.length})`;

  return (
    <MenuDeContexto ponto={ponto} rotulo="Menu da mensagem" onFechar={onFechar}>
      <DropdownItem disabled={!sozinha} onSelect={() => responder('responder')}>
        Responder
      </DropdownItem>
      <DropdownItem disabled={!sozinha} onSelect={() => responder('responder_a_todos')}>
        Responder a todos
      </DropdownItem>
      <DropdownItem disabled={!sozinha} onSelect={() => responder('encaminhar')}>
        Encaminhar
      </DropdownItem>
      <DropdownSeparator />
      <DropdownItem onSelect={() => void executar(ids, conversa.unread ? 'read' : 'unread')}>
        {conversa.unread ? 'Marcar como lida' : 'Marcar como não lida'}
        {sufixo}
      </DropdownItem>
      <DropdownItem onSelect={() => void executar(ids, conversa.flagged ? 'unflag' : 'flag')}>
        {conversa.flagged ? 'Remover dos favoritos' : 'Favoritar'}
        {sufixo}
      </DropdownItem>
      <DropdownItem onSelect={() => void executar(ids, 'archive', { aoConcluir: aoSair })}>
        Arquivar{sufixo}
      </DropdownItem>
      <DropdownItem
        disabled={!sozinha}
        onSelect={() =>
          useUi.getState().pedirTarefa({ tipo: 'criar', messageId: id, assunto: conversa.subject })
        }
      >
        Criar tarefa
      </DropdownItem>
      <DropdownItem
        disabled={!sozinha || remetente === null}
        onSelect={() =>
          remetente !== null &&
          useUi.getState().pedirRegra({ accountId: conversa.accountId, remetente })
        }
      >
        Criar regra
      </DropdownItem>
      <DropdownSeparator />
      <DropdownLabel>Mover para</DropdownLabel>
      {destinos.length === 0 ? (
        <DropdownItem disabled>Nenhuma outra pasta</DropdownItem>
      ) : (
        destinos.map((p) => (
          <DropdownItem
            key={p.pasta.id}
            onSelect={() => void executar(ids, 'move', { destino: p.pasta.id, aoConcluir: aoSair })}
          >
            {p.rotulo}
          </DropdownItem>
        ))
      )}
      <DropdownSeparator />
      <DropdownLabel>Adiar até</DropdownLabel>
      {OPCOES_DE_ADIAMENTO.map((o) => (
        <DropdownItem key={o.id} onSelect={() => adiarPara(o.id)}>
          {o.rotulo}
        </DropdownItem>
      ))}
      <DropdownSeparator />
      <DropdownLabel>Acompanhar se não houver resposta</DropdownLabel>
      {OPCOES_DE_ADIAMENTO.map((o) => (
        <DropdownItem key={o.id} onSelect={() => acompanharPara(o.id)}>
          {o.rotulo}
        </DropdownItem>
      ))}
      <DropdownSeparator />
      <DropdownItem danger onSelect={() => void executar(ids, 'spam', { aoConcluir: aoSair })}>
        Marcar spam{sufixo}
      </DropdownItem>
      <DropdownItem danger onSelect={() => void executar(ids, 'delete', { aoConcluir: aoSair })}>
        Excluir{sufixo}
      </DropdownItem>
    </MenuDeContexto>
  );
}
